"use client";

import {
  ENTP_PA_MEDICATION_ASSIGNMENT,
  ENTP_PA_PROCEDURE_ASSIGNMENT,
  hasEntpOfficeVisitCptCodes,
} from "@/configuration/groups/entpartners/configuration";
import { ENTP_CHICAGO_ECW_LOGIN_URL } from "@/configuration/groups/entpartners/referral-instructions";
import { CopyableContent } from "@/src/components/copyable-content";
import { entpCptToStaff } from "@/src/data/entp-cpt-staff-mapping";
import { generatePriorAuthFilename } from "@/src/lib/utils/filename";
import {
  PriorAuthorizationStatus,
  PriorAuthorizationType,
} from "@/src/models/documents/prior-authorization/prior-authorization";
import { Task } from "@/src/models/task";

type StaffAssignment = Record<string, string>;

const DEFAULT_NO_ASSIGNEE = "No Assignee found for CPT";

const listClass = "list-disc ml-3 flex flex-col gap-0.5";

function getNameInstructions(
  status: PriorAuthorizationStatus,
  type: PriorAuthorizationType,
  numberOfServices: number | null,
  date: string | null
) {
  return (
    <>
      <p>eCW:</p>
      <a href={ENTP_CHICAGO_ECW_LOGIN_URL} target="_blank">
        {ENTP_CHICAGO_ECW_LOGIN_URL}
      </a>
      <p>Go to the patient&apos;s documents and select the &quot;Authorization folder&quot;.</p>
      <ul className={listClass}>
        <li>
          <CopyableContent text={generatePriorAuthFilename(null, date, status, type, numberOfServices)} />
        </li>
        <li>
          The date should be the date of the payer update, i.e. the sent date at the top of the letterhead. If it&apos;s
          not clear or the document does not have a date, then put the approved date.
        </li>
        {type !== PriorAuthorizationType.MEDICATION ? (
          <li>If the approval is for a number of visits, add a number, e.g. 2025.02.20 Auth Approved 12 visits</li>
        ) : null}
      </ul>
    </>
  );
}

function getStaffAssignment(staffAssignment: StaffAssignment) {
  return (
    <ul className={listClass}>
      {Object.keys(staffAssignment).length > 0 ? (
        Object.entries(staffAssignment).map(([cptCode, staff], idx) => (
          <li key={idx}>
            {cptCode} → <CopyableContent text={staff} />
          </li>
        ))
      ) : (
        <li>No CPTs found</li>
      )}
    </ul>
  );
}

export function PriorAuthorizationInstruction({ task }: { task: Task }) {
  const priorAuth = task.priorAuthorization;
  const status = priorAuth?.authorizationStatus ?? PriorAuthorizationStatus.OTHER;
  const type = priorAuth?.authorizationType ?? null;
  const provider = task.priorAuthorization?.practitioner;
  const providerFamilyName = provider?.familyName?.toLowerCase();

  const cptCodes = priorAuth?.commonProcedureTerminologyList?.map((cpt: { code: string }) => cpt.code) ?? [];

  const staffAssignment: StaffAssignment = cptCodes.reduce(
    (acc: StaffAssignment, cptCode: string) => ({
      ...acc,
      [cptCode]: entpCptToStaff[cptCode] || DEFAULT_NO_ASSIGNEE,
    }),
    {}
  );

  const noProviderMedicationAssignment = (
    <>
      <p>Assignment Depends on type of medication:</p>
      <ul className={listClass}>
        <li>
          Allergy = <CopyableContent text="Cuzco, Janice" />{" "}
        </li>
        <li>
          ENT = <CopyableContent text="User, Triage" />{" "}
        </li>
        <li>
          Sleep = <CopyableContent text="Nikolayeva, Nina" />{" "}
        </li>
        <li>
          Audiology = <CopyableContent text="Gamez, Emily" />{" "}
        </li>
        <li>
          SubOto = <CopyableContent text="Sub Sanchez, Rafaela" />{" "}
        </li>
      </ul>
    </>
  );

  const medicationAssignment = providerFamilyName ? (
    <CopyableContent text={ENTP_PA_MEDICATION_ASSIGNMENT[providerFamilyName] || "User, Triage"} />
  ) : (
    noProviderMedicationAssignment
  );

  const medicationDispositionInstructions = (
    <>
      <p>
        Provider is {provider?.familyName} assign to {medicationAssignment}
      </p>
    </>
  );

  const procedureAssignment =
    providerFamilyName && providerFamilyName in ENTP_PA_PROCEDURE_ASSIGNMENT ? (
      <CopyableContent text={ENTP_PA_PROCEDURE_ASSIGNMENT[providerFamilyName]} />
    ) : (
      getStaffAssignment(staffAssignment)
    );

  const procedureAssignmentInstructions = (
    <>
      <p>
        Provider is {provider?.familyName} assign to {procedureAssignment}
      </p>
    </>
  );

  const dispositionInstructions = (() => {
    // Check for office visits first
    if (hasEntpOfficeVisitCptCodes(cptCodes)) {
      if (status === PriorAuthorizationStatus.APPROVED) {
        return <p>Mark the Document as Reviewed</p>;
      }

      if (status === PriorAuthorizationStatus.DENIED) {
        return (
          <p>
            Provider is {provider?.familyName} assign to <CopyableContent text="User, Scheduler" />
          </p>
        );
      }
    }

    // Handle procedure
    if (type === PriorAuthorizationType.PROCEDURE) {
      if (status === PriorAuthorizationStatus.APPROVED) {
        return <p>Mark the Document as Reviewed</p>;
      }

      return (
        <p>
          Assign to <CopyableContent text="Grant, Laura" />
        </p>
      );
    }

    // Handle medication type
    if (type === PriorAuthorizationType.MEDICATION) {
      return medicationDispositionInstructions;
    }

    // Default: procedure assignment instructions for other cases
    return procedureAssignmentInstructions;
  })();

  return (
    <div className="flex flex-col gap-2">
      {getNameInstructions(status, type, priorAuth?.numberOfServices, priorAuth?.dateOfDocument)}
      {dispositionInstructions}
    </div>
  );
}
